import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { Checkbox } from '@/shared/ui/checkbox';
import type { TreeLabelSegment, TreeRowAction, TreeViewRow } from './treeTypes';
import { findMatchRanges } from './treeViewModel';
import { TreeChevronIcon, TreeFunnelIcon } from './treeIcons';
import { LEVEL_TEXT_CLASSES, levelIndex } from './treeLevelStyles';

interface TreeViewRowProps {
  row: TreeViewRow;
  actions: TreeRowAction[];
  typeIcon?: React.FC<{ className?: string }>;
  /** Normalizovaný dotaz hledání pro zvýraznění shody v textu */
  normalizedQuery: string;
  onToggleExpand: (nodeId: string) => void;
  onActivate: (nodeId: string) => void;
  /** Toggle odkrytí profiltrovaného výpisu (klik na štítek) */
  onToggleReveal: (nodeId: string) => void;
  onCheckToggle?: (nodeId: string) => void;
  /** Klik na akci řádku uzel navíc vybere (bez plné aktivace) */
  onActionSelect?: (nodeId: string) => void;
  isFocused: boolean;
}

/**
 * Zvýraznění shody hledání: podbarví se jen nalezený podřetězec (PHX-TREE-01 § 2),
 * nikdy celý řádek.
 */
function TreeViewHighlight({ text, normalizedQuery }: { text: string; normalizedQuery: string }) {
  const ranges = normalizedQuery ? findMatchRanges(text, normalizedQuery) : [];
  if (ranges.length === 0) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const [start, end] of ranges) {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <mark
        key={start}
        className="rounded-phx-xs bg-[color-mix(in_srgb,var(--phx-draft)_38%,transparent)] px-px text-inherit"
      >
        {text.slice(start, end)}
      </mark>
    );
    cursor = end;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

const SEGMENT_CLASSES: Record<TreeLabelSegment['variant'], string> = {
  strong: 'font-medium',
  muted: 'text-phx-text-muted',
  data: 'text-phx-text-2 tabular-nums',
};

/**
 * Řádek tree-view dle anatomie PHX-TREE-01: výška 26 px, odsazení 16 px
 * na úroveň, chevron 18×18 (list = pevný spacer), ikona obarvená dle hloubky,
 * štítek částečné shody a editační akce viditelné na hover.
 */
export function TreeViewRowComponent({
  row,
  actions,
  typeIcon: TypeIcon,
  normalizedQuery,
  onToggleExpand,
  onActivate,
  onToggleReveal,
  onCheckToggle,
  onActionSelect,
  isFocused,
}: TreeViewRowProps) {
  const { t } = useTranslation('sharedUi');
  const { node, depth, isExpanded, hasChildren, isActive, chip, checkState } = row;

  const applicableActions = actions.filter(
    (a) => !a.appliesTo || a.appliesTo.length === 0 || a.appliesTo.includes(node.type)
  );

  return (
    <div
      role="treeitem"
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-selected={isActive}
      aria-level={depth + 1}
      tabIndex={isFocused ? 0 : -1}
      data-node-id={node.id}
      className={cn(
        'group rounded-phx text-phx-text text-phx-md flex h-5 items-center gap-1.25 pr-1 outline-none',
        'focus-visible:shadow-phx-focus',
        isActive ? 'bg-phx-primary-tint' : 'hover:bg-phx-surface-2'
      )}
      style={{ paddingLeft: `${depth * 16 + 2}px` }}
      onClick={() => onActivate(node.id)}
      onDoubleClick={() => {
        if (hasChildren) onToggleExpand(node.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate(node.id);
        }
      }}
    >
      {/* Expander — u listu pevná mezera 18 px, aby řádky lícovaly */}
      {hasChildren ? (
        <button
          type="button"
          tabIndex={-1}
          className="rounded-phx-sm text-phx-text-muted hover:bg-phx-surface-3 hover:text-phx-text flex h-4.5 w-4.5 shrink-0 cursor-pointer items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(node.id);
          }}
          aria-label={isExpanded ? t('treeView.collapseRow') : t('treeView.expandRow')}
        >
          <TreeChevronIcon className={cn('transition-transform', isExpanded && 'rotate-90')} />
        </button>
      ) : (
        <span className="h-4.5 w-4.5 shrink-0" aria-hidden />
      )}

      {/* Checkbox (jen v checkable režimu) */}
      {checkState !== undefined && onCheckToggle && (
        <span
          className="flex shrink-0 items-center"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={checkState === 'checked'}
            indeterminate={checkState === 'indeterminate'}
            onCheckedChange={() => onCheckToggle(node.id)}
            className="[&_.phx-check-box]:h-3.25 [&_.phx-check-box]:w-3.25"
          />
        </span>
      )}

      {/* Typová ikona — barva vázaná na hloubku úrovně */}
      {TypeIcon && <TypeIcon className={cn('shrink-0', LEVEL_TEXT_CLASSES[levelIndex(depth)])} />}

      {/* Label — segmenty, nebo kód + label */}
      <span className="min-w-0 truncate">
        {node.labelSegments?.length ? (
          node.labelSegments.map((segment, index) => (
            <Fragment key={index}>
              {index > 0 && ' '}
              <span className={SEGMENT_CLASSES[segment.variant]}>
                <TreeViewHighlight text={segment.text} normalizedQuery={normalizedQuery} />
              </span>
            </Fragment>
          ))
        ) : (
          <>
            {node.code != null && (
              <span className="mr-phx-2 font-medium tabular-nums">
                <TreeViewHighlight text={node.code} normalizedQuery={normalizedQuery} />
              </span>
            )}
            <span className={node.code != null ? 'text-phx-text-muted' : ''}>
              <TreeViewHighlight text={node.label} normalizedQuery={normalizedQuery} />
            </span>
          </>
        )}
      </span>

      {/* Štítek částečné shody (PHX-TREE-01 § 5) */}
      {chip && (
        <button
          type="button"
          tabIndex={-1}
          className={cn(
            'rounded-phx-sm text-phx-2xs flex h-4.25 shrink-0 cursor-pointer items-center gap-0.75 border px-1.25 font-medium tabular-nums',
            chip.revealed
              ? 'bg-phx-primary-tint text-phx-primary border-[color-mix(in_srgb,var(--phx-primary)_40%,transparent)]'
              : 'bg-phx-draft-tint text-phx-warning hover:border-phx-draft border-[color-mix(in_srgb,var(--phx-draft)_40%,transparent)]'
          )}
          title={
            chip.revealed
              ? t('treeView.chipAllTitle')
              : t('treeView.chipFilteredTitle', { total: chip.total })
          }
          onClick={(e) => {
            e.stopPropagation();
            onToggleReveal(node.id);
          }}
        >
          <TreeFunnelIcon />
          {chip.revealed
            ? t('treeView.chipAll', { total: chip.total })
            : t('treeView.chipFiltered', { shown: chip.shown, total: chip.total })}
        </button>
      )}

      {/* Row akce — vždy viditelné, zvýrazní se na hover */}
      {applicableActions.length > 0 && (
        <div className="gap-phx-2 ml-auto flex shrink-0">
          {applicableActions.map((action) => {
            const ActionIcon = action.icon;
            return (
              <button
                key={action.id}
                type="button"
                tabIndex={-1}
                className="text-phx-text-muted hover:bg-phx-surface-3 hover:text-phx-text rounded-phx-sm flex h-5 w-5 cursor-pointer items-center justify-center opacity-60 group-hover:opacity-80 hover:opacity-100! focus-visible:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  // Klik na akci uzel navíc vybere (zvýrazní) — bez plné aktivace/navigace.
                  onActionSelect?.(node.id);
                  action.onClick(node.id);
                }}
                aria-label={action.label}
              >
                {ActionIcon ? (
                  <ActionIcon className="h-2.75 w-2.75" />
                ) : (
                  <span className="text-phx-xs">{action.label}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
